import { ROUTES } from "@/routes";
import { primaryColor } from "@/theme";
import { Chip, List, ListItem, ListItemIcon, ListItemText, Paper, Divider, Typography, Box, ListItemButton, Collapse, Avatar } from "@mui/material";
import { Link, useMatch } from "react-router-dom";
import ListItemButtonSelected from "./ListItemButtonSelected";
import IconAppartment from "@mui/icons-material/Apartment";
import LayersIcon from "@mui/icons-material/Layers";
import DashboardIcon from "@mui/icons-material/Dashboard";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDataQueueCountQuery } from "@/helpers/getDataQueueCount";
import { useSelector } from "react-redux";
import userSelector from "@/store/selectors/userSelector";
import { useGetBuildingsExtended } from "@/helpers/getBuildingsExtended";
import SidebarIcon from "./SidebarIcon";
import { useState } from "react";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import SolarPowerOutlinedIcon from '@mui/icons-material/SolarPowerOutlined';
import { useGetActiveMunicipality } from "@/helpers/getMunicipality";
import { useGetBuildingGroups } from "@/helpers/getBuildingGroups";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { IconButton } from "@mui/material";
import { getBuildingGroupImageUrl, getBuildingImageUrl } from "@/helpers/imageUrls";
import { canPerformClientAction } from "@/helpers/clientPermissions";

const getBuildingInitials = (name: string) => {
  if (!name) return "";
  const words = name.split(' ').filter(w => w.length > 0);
  return words.slice(0, 3).map(w => w[0]).join('');
};

export const SidebarClientButtons = ({ isCollapsed }: { isCollapsed?: boolean }) => {
  const user = useSelector(userSelector);
  const canWriteConsumptions = canPerformClientAction(user?.clientRole, 'consumption_write', user?.primaryUser);
  const canWriteInvoices = canPerformClientAction(user?.clientRole, 'invoice_write', user?.primaryUser);
  const canFullyEdit = canPerformClientAction(user?.clientRole, 'full_write', user?.primaryUser);
  const { data } = useGetBuildingsExtended()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [ungroupedExpanded, setUngroupedExpanded] = useState<boolean | undefined>();
  const activeBuildingId = useMatch(ROUTES.CLIENT_BUILDING_DETAILS)?.params.buildingId;
  const municipalityId = useGetActiveMunicipality();

  const { data: groups } = useGetBuildingGroups();

  const groupedBuildingIds = new Set(
    (groups ?? []).flatMap((group) => (group.buildings ?? []).map((building) => building.id))
  );
  const buildingOrderMap = new Map((data ?? []).map((building, index) => [building.id, index]));
  const ungroupedBuildings = (data ?? []).filter((building) => !groupedBuildingIds.has(building.id));
  const isActiveBuildingUngrouped = ungroupedBuildings.some((building) => building.id === activeBuildingId);
  const ungroupedIsExpanded = ungroupedExpanded ?? isActiveBuildingUngrouped;
  const hasAnyCollectionPoint = (data ?? []).some((building) => (building.collectionPoints?.length ?? 0) > 0);

  const { data: dataQueueCount } = useDataQueueCountQuery({ enabled: !!user?.name && !!user?.verifiedByAdmin && !!user?.verifiedEmail });

  return (
    <>
      <ListItemButtonSelected component={Link} to={ROUTES.DASHBOARD} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={DashboardIcon} />
        </ListItemIcon>
        <ListItemText primary="Přehled" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_SETTINGS} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={SettingsIcon} />
        </ListItemIcon>
        <ListItemText primary="Nastavení" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_SUBSCRIPTION} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={PaymentsOutlinedIcon} />
        </ListItemIcon>
        <ListItemText primary="Předplatné" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_BUILDING_GROUPS} isCollapsed={isCollapsed}>
        <ListItemIcon>
          <SidebarIcon component={LayersIcon} />
        </ListItemIcon>
        <ListItemText primary="Skupiny objektů" />
      </ListItemButtonSelected>

      <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_SOLAR_MANAGEMENT} isCollapsed={isCollapsed}>
        <ListItemIcon><SidebarIcon component={SolarPowerOutlinedIcon} /></ListItemIcon>
        <ListItemText primary="Fotovoltaika" />
      </ListItemButtonSelected>

      {((hasAnyCollectionPoint && canWriteConsumptions) || canWriteInvoices) && (
        <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_UPLOAD_DATA} isCollapsed={isCollapsed}>
          <ListItemIcon>
            <SidebarIcon component={UploadFileIcon} />
          </ListItemIcon>
          <ListItemText primary="Nahrát data" />
        </ListItemButtonSelected>
      )}

      {canWriteConsumptions && (
        <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_DATA_IMPORT} isCollapsed={isCollapsed}>
          <ListItemIcon><SidebarIcon component={UploadFileIcon} /></ListItemIcon>
          <ListItemText primary="Import dat" />
        </ListItemButtonSelected>
      )}


      <Box sx={{ mt: 2 }}>
        <ListItem>
          <ListItemIcon>
            <SidebarIcon component={IconAppartment} />
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Objekty
              </Typography>
            }
          />
        </ListItem>
        <Divider sx={{ mx: 2, my: 1 }} />
      </Box>

      <List disablePadding>
        {canFullyEdit && (
          <ListItemButtonSelected component={Link} to={ROUTES.CLIENT_ADD_BUILDING} urlLevel={3} isCollapsed={isCollapsed}>
            <ListItemIcon>
              <SidebarIcon component={AddIcon} />
            </ListItemIcon>
            <ListItemText primary="Přidat objekt" />
          </ListItemButtonSelected>
        )}
        {(groups ?? []).map((group) => {
          const buildingsInGroup = [...(group.buildings ?? [])].sort((a, b) => {
            const orderA = buildingOrderMap.get(a.id) ?? Number.MAX_SAFE_INTEGER;
            const orderB = buildingOrderMap.get(b.id) ?? Number.MAX_SAFE_INTEGER;
            return orderA - orderB;
          });
          if (buildingsInGroup.length === 0) return null;
          const isActiveBuildingGroup = buildingsInGroup.some((building) => building.id === activeBuildingId);
          const isExpanded = expandedGroups[group.id] ?? isActiveBuildingGroup;
          return (
            <Box key={group.id}>
              <Divider sx={{ mx: 2, my: 0.5, opacity: 0.5 }} />
              <ListItem disablePadding sx={{ px: isCollapsed ? 0 : 1, py: 0.25 }}>
                <ListItemButton
                  className="sidebar-item-level-2"
                  aria-expanded={isExpanded}
                  sx={{
                    pr: 1,
                    borderRadius: '0 20px 20px 0',
                    '&:hover': {
                      backgroundColor: primaryColor,
                      color: 'white',
                      '& .MuiListItemIcon-root': {
                        color: 'white !important',
                      },
                      '& .MuiSvgIcon-root': {
                        color: 'white !important',
                      },
                      '& .MuiListItemText-primary': {
                        color: 'white',
                      },
                      '& .MuiListItemText-secondary': {
                        color: 'rgba(255,255,255,0.85)',
                      }
                    }
                  }}
                  onClick={() => setExpandedGroups((prev) => ({ ...prev, [group.id]: !(prev[group.id] ?? isActiveBuildingGroup) }))}
                >
                  <ListItemIcon sx={{ minWidth: 28, transition: 'margin 0.2s' }}>
                    <Avatar
                      src={getBuildingGroupImageUrl(group, municipalityId)}
                      sx={{ width: 24, height: 24, bgcolor: primaryColor, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                    >
                      {!group.image && getBuildingInitials(group.name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={group.name}
                    secondary={`Objektů: ${buildingsInGroup.length}`}
                    primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '0.875rem' } }}
                    secondaryTypographyProps={{ sx: { fontSize: '0.75rem' } }}
                  />
                  {isExpanded ? <ExpandLessIcon className="sidebar-expand-icon" fontSize="small" /> : <ExpandMoreIcon className="sidebar-expand-icon" fontSize="small" />}
                </ListItemButton>
              </ListItem>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                {buildingsInGroup.map((building) => (
                  <ListItemButtonSelected
                    key={building.id}
                    className="sidebar-item-level-3"
                    sx={{
                      py: 1,
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        '& .MuiChip-root': {
                          transform: 'scale(1.05)',
                          transition: 'transform 0.2s ease-in-out'
                        }
                      }
                    }}
                    component={Link}
                    urlLevel={3}
                    to={ROUTES.CLIENT_BUILDING_DETAILS.replace(":buildingId", building.id)}
                    isCollapsed={isCollapsed}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <Avatar
                        src={getBuildingImageUrl(building)}
                        sx={{ width: 24, height: 24, bgcolor: primaryColor, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                      >
                        {!building.image && getBuildingInitials(building.name)}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={building.name}
                      primaryTypographyProps={{
                        sx: { fontWeight: 500, fontSize: '0.9rem' }
                      }}
                    />
                    {dataQueueCount?.[building.id] && (
                      <Chip
                        className="sidebar-chip"
                        label={dataQueueCount[building.id].count}
                        size="small"
                        sx={{
                          backgroundColor: primaryColor,
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          height: 20,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    )}
                  </ListItemButtonSelected>
                ))}
              </Collapse>
            </Box>
          );
        })}

        {ungroupedBuildings.length > 0 && (
          <>
            <Divider sx={{ mx: 2, my: 0.5, opacity: 0.5 }} />
            <ListItem disablePadding sx={{ px: isCollapsed ? 0 : 1, py: 0.25 }}>
              <ListItemButton
                className="sidebar-item-level-2"
                aria-expanded={ungroupedIsExpanded}
                sx={{
                  pr: 1,
                  borderRadius: '0 20px 20px 0',
                  '&:hover': {
                    backgroundColor: primaryColor,
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white !important',
                    },
                    '& .MuiSvgIcon-root': {
                      color: 'white !important',
                    },
                    '& .MuiListItemText-primary': {
                      color: 'white',
                    }
                  }
                }}
                onClick={() => setUngroupedExpanded((prev) => !(prev ?? isActiveBuildingUngrouped))}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <SidebarIcon component={IconAppartment} />
                </ListItemIcon>
                <ListItemText
                  primary="Nezařazené"
                  primaryTypographyProps={{ sx: { fontWeight: 600, fontSize: '0.875rem' } }}
                />
                {ungroupedIsExpanded ? <ExpandLessIcon className="sidebar-expand-icon" fontSize="small" /> : <ExpandMoreIcon className="sidebar-expand-icon" fontSize="small" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={ungroupedIsExpanded} timeout="auto" unmountOnExit>
              {ungroupedBuildings.map((building) => (
                <ListItemButtonSelected
                  key={building.id}
                  className="sidebar-item-level-3"
                  sx={{
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      '& .MuiChip-root': {
                        transform: 'scale(1.05)',
                        transition: 'transform 0.2s ease-in-out'
                      }
                    }
                  }}
                  component={Link}
                  urlLevel={3}
                  to={ROUTES.CLIENT_BUILDING_DETAILS.replace(":buildingId", building.id)}
                  isCollapsed={isCollapsed}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Avatar
                      src={getBuildingImageUrl(building)}
                      sx={{ width: 24, height: 24, bgcolor: primaryColor, color: 'white', fontSize: '0.75rem', fontWeight: 'bold' }}
                    >
                      {!building.image && getBuildingInitials(building.name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={building.name}
                    primaryTypographyProps={{
                      sx: { fontWeight: 500, fontSize: '0.9rem' }
                    }}
                  />
                  {dataQueueCount?.[building.id] && (
                    <Chip
                      className="sidebar-chip"
                      label={dataQueueCount[building.id].count}
                      size="small"
                      sx={{
                        backgroundColor: primaryColor,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 20,
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  )}
                </ListItemButtonSelected>
              ))}
            </Collapse>
          </>
        )}
      </List>
    </>
  );
};

interface SidebarProps {
  isCollapsed?: boolean;
  onToggle?: () => void;
}

const Sidebar = ({ isCollapsed, onToggle }: SidebarProps) => {
  return (
    <Box sx={{
      width: isCollapsed ? 64 : 260,
      minWidth: 0,
      flexShrink: 0,
      transition: 'width 0.3s ease',
      position: 'relative',
      zIndex: 10,
    }}>
      <Paper
        elevation={isCollapsed ? 4 : 3}
        sx={{
          width: 260,
          boxSizing: "border-box",
          height: "100%",
          borderRadius: 0,
          background: (theme) => theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`
            : `linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(248, 250, 252, 1) 100%)`,
          borderRight: 1,
          borderRightColor: 'divider',
          boxShadow: isCollapsed ? `4px 0px 12px rgba(0, 0, 0, 0.15)` : `
            0px 2px 8px rgba(0, 0, 0, 0.08),
            4px 0px 12px rgba(0, 0, 0, 0.04)
          `,
          paddingTop: "16px",
          paddingBottom: "16px",
          overflowX: "hidden",
          whiteSpace: "nowrap",
          // Clip the full-width sidebar while collapsed instead of reflowing every row on hover.
          clipPath: isCollapsed ? 'inset(0 196px 0 0)' : 'inset(0 0 0 0)',
          transition: 'clip-path 0.25s ease',
          // Keep building icons visible in the 64 px rail; padding changes only on explicit toggle.
          '& .sidebar-item-level-2': { paddingLeft: isCollapsed ? '16px !important' : '24px !important' },
          '& .sidebar-item-level-3': { paddingLeft: isCollapsed ? '16px !important' : '72px !important' },
          '& .MuiListItemText-root': { opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' },
          '& .sidebar-chip': { visibility: isCollapsed ? 'hidden' : 'visible' },
          '& .sidebar-expand-icon': { opacity: isCollapsed ? 0 : 1, transition: 'opacity 0.2s' },
          '&:hover, &:focus-within': isCollapsed ? {
            clipPath: 'inset(0 0 0 0)',
            '& .MuiListItemText-root': { opacity: '1 !important' },
            '& .sidebar-chip': { visibility: 'visible !important' },
            '& .sidebar-expand-icon': { opacity: '1 !important' },
          } : {},
          '& .MuiListItemButton-root': {
            borderRadius: '0 24px 24px 0',
            margin: '2px 8px 2px 0',
            paddingLeft: '16px',
            paddingRight: '16px',
            transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: primaryColor,
              transform: 'translateX(4px)',
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white !important',
              },
              '& .MuiSvgIcon-root': {
                color: 'white !important',
              },
            },
            '&.Mui-selected': {
              backgroundColor: primaryColor,
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
              '& .MuiListItemText-primary': {
                color: 'white',
                fontWeight: 600,
              },
              '&:hover': {
                backgroundColor: primaryColor,
                transform: 'translateX(4px)',
                boxShadow: `0 4px 12px rgba(25, 118, 210, 0.3)`,
              }
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', px: 1, mb: 1 }}>
          <IconButton onClick={onToggle} size="small" sx={{ color: 'text.secondary' }}>
            {isCollapsed ? <KeyboardDoubleArrowRightIcon /> : <KeyboardDoubleArrowLeftIcon />}
          </IconButton>
        </Box>
        <List sx={{ padding: 0 }}>
          <SidebarClientButtons isCollapsed={isCollapsed} />
        </List>
      </Paper>
    </Box>
  );
};

export default Sidebar;
